export const tfOrdStatus = {
  Draft: "badge-secondary",
  Created: "badge-primary",
  "Not Allocated": "badge-light-info",
  "Partially Allocated": "badge-light-info",
  "Fully Allocated": "badge-info",
  "Partially Cargo Ready": "badge-light-success",
  "Fully Cargo Ready": "badge-success",
  "Partially Dispatched": "badge-light-success",
  "Fully Dispatched": "badge-success",
  "Partially Received": "badge-light-success",
  "Partial Receipt Created": "badge-light-success",
  "Fully Received": "badge-success",
  "Full Receipt Created": "badge-success",
  "Partially Receipted": "badge-light-success",
  "Partial Receipt Approved": "badge-light-success",
  "Fully Receipted": "badge-success",
  "Full Receipt Approved": "badge-success",
  Completed: "badge-success",
  Cancelled: "badge-danger",
};

export const tfOrdLineStatus = {
  Draft: "badge-secondary",
  Created: "badge-primary",
  "Not Allocated": "badge-light-info",
  "Partially Allocated": "badge-light-info",
  "Fully Allocated": "badge-info",
  "Cargo Ready": "badge-light-success",
  "Partially Dispatched": "badge-light-success",
  "Fully Dispatched": "badge-success",
  "Partially Received": "badge-light-success",
  "Partial Receipt Created": "badge-light-success",
  "Fully Received": "badge-success",
  "Full Receipt Created": "badge-success",
  "Partially Receipted": "badge-light-success",
  "Partial Receipt Approved": "badge-light-success",
  "Fully Receipted": "badge-success",
  "Full Receipt Approved": "badge-success",
  Completed: "badge-success",
  Cancelled: "badge-danger",
};

const ORANGE_STATUS = Object.freeze([
  "Awaiting Buyer Response",
  "Changes Proposed",
  "Draft Sent",
  "Departed",
  "In Active", // [Product]
  "Not Consolidated",
  "Partial Receipt Approved",
  "Partial Receipt Created",
  "Partially Allocated",
  "Partially Paid",
  "Partially Approved",
  "Partially Arrived",
  "Partially Arrived at Discharge Port",
  "Partially Accepted by Carrier",
  "Partially Consolidated",
  "Partially Departed",
  "Partially Departed Loading Port",
  "Partially Discharged",
  "Partially Dispatched",
  "Partially Final Destination",
  "Partially Gate In at Loading Port",
  "Partially Gate Out at Discharge Port",
  "Partially Loaded at Loading Port",
  "Partially Manifested",
  "Partially Received from Flight",
  "Partially Released",
  "Partially Transshipment",
  "Partially ASN Validated",
  "Partially ASN Created",
  "Supplier Reviewing",
  "In Production",
  "Released",
  "Partially Picked",
  "Partially Ready to Pack",
  "Partially Packed",
  "Partially Ready For Dispatch",
  "Partially Received",
  "On Hold",
  "Warning",
  "Prospect",
  "Released",
  "Awaiting Authorisation",
  "Scheduled",
  "Departed",
  "Low Stock",
  "Booked",
]);

const GREEN_STATUS = Object.freeze([
  "Approved",
  "Accepted",
  "Active", // [Product]
  "Completed",
  "Consolidated",
  "Created",
  "Full Receipt Approved",
  "Full Receipt Created",
  "Fully Allocated",
  "Fully Consolidated",
  "Fully Dispatched",
  "Invoice Approved",
  "Invoiced",
  "Paid",
  "Processed",
  "Received",
  "Sent",
  "Fully Picked",
  "Fully Ready to Pack",
  "Packed",
  "Fully Packed",
  "Fully Ready For Dispatch",
  "Ready For Dispatch",
  "Ready for Allocation",
  "Sellable",
  "In Stock",
  "Fully Received",
  "Fully Receipted",
]);

const RED_STATUS = Object.freeze([
  "Cancelled",
  "Changes Rejected",
  "In Active", // [Product]
  "Blacklisted",
  "Voided",
  "Rejected",
  "No Stock",
  "Error",
  "Expired",
  "Inactive",
]);

const BLUE_STATUS = Object.freeze([
  "Accepted by Carrier",
  "Arrived",
  "Arrived at Discharge Port",
  "ASN Created",
  "ASN Validated",
  "Cargo Ready",
  "Confirmed",
  "Departed",
  "Departed Loading Port",
  "Discharged",
  "Final Destination",
  "Gate In at Loading Port",
  "Gate Out at Discharge Port",
  "In Transit",
  "Loaded at Loading Port",
  "Manifested",
  "Not Stocked", // [Product]
  "Received from Flight",
  "Released",
  "Seller Location",
  "Transshipment",
  "Authorised",
  "Allocated",
]);

const GREY_STATUS = Object.freeze(["Closed", "Draft", "Not Allocated", "Planned", "Unpaid"]);

const YELLOW_STATUS = Object.freeze(["Pending", "Invited"]);

/** Fallback if no match is found */
const DEFAULT_COLOR = "grey";

/** Build a single lookup map for constant‑time resolution (keys are lowercased). */
const STATUS_COLOR_MAP = new Map([
  ...ORANGE_STATUS.map(status => [status.toLowerCase(), "orange"]),
  ...GREEN_STATUS.map(status => [status.toLowerCase(), "green"]),
  ...RED_STATUS.map(status => [status.toLowerCase(), "red"]),
  ...BLUE_STATUS.map(status => [status.toLowerCase(), "blue"]),
  ...GREY_STATUS.map(status => [status.toLowerCase(), "grey"]),
  ...YELLOW_STATUS.map(status => [status.toLowerCase(), "yellow"]),
]);

Object.freeze(STATUS_COLOR_MAP);

/**
 * Get the CSS color class for a given status.
 *
 * @param {string} status — the status string to classify
 * @returns {'orange'|'green'|'red'|'blue'|'grey'|'yellow'} the color name (falls back to DEFAULT_COLOR)
 */
export function getClassForStatus(status) {
  const normalizedStatus = String(status ?? "")
    .trim()
    .toLowerCase();
  if (!normalizedStatus) {
    return DEFAULT_COLOR;
  }
  return STATUS_COLOR_MAP.get(normalizedStatus) || DEFAULT_COLOR;
}

const DELETABLE_PO_LINE_STATUSES = new Set([
  "Draft",
  "Created",
  "Sent",
  "Accepted",
  "Scheduled",
  "In Production",
  "Ready for Allocation",
  "Not Allocated",
  "Partially Allocated",
  "Fully Allocated",
]);

/**
 * Purchase order lines past allocated (cargo ready, dispatched, receipted,
 * invoiced, or cancelled) cannot be removed from the edit form.
 *
 * New unsaved rows have no status and remain deletable.
 */
export function canDeletePurchaseOrderLine(status) {
  if (!status) return true;
  return DELETABLE_PO_LINE_STATUSES.has(status);
}
